/**
 * Collects context about the visitor who submits the enquiry form, so a lead
 * in the admin says where it came from instead of just who sent it.
 *
 * Two things are captured:
 *
 *   1. First touch of the visit — the referrer and any UTM/click ids that were
 *      on the URL when the visitor arrived. These are only present on the very
 *      first page load, so they're stashed in sessionStorage before an in-app
 *      navigation loses them.
 *   2. Microsoft Clarity's identifiers, plus a `lead_ref` custom tag we set on
 *      the session. Clarity has no API for fetching a single recording, so
 *      these are what the admin uses to find the session in the dashboard.
 *
 * Everything here is best-effort. Storage can throw (Safari private mode),
 * Clarity can be blocked, and the visitor may have arrived with no referrer at
 * all — in every case we send less rather than breaking the submission.
 */

const SESSION_KEY = "id_lead_session_v1";
const VISITOR_KEY = "id_lead_visitor_v1";

// How long we're willing to wait for Clarity before submitting without it.
const CLARITY_TIMEOUT_MS = 1200;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const CLICK_ID_KEYS = ["gclid", "fbclid", "msclkid"];

// react-snap pre-renders in a headless Chrome; nothing it "visits" is a real
// session, so attribution stays switched off during the build.
const isPrerendering = () =>
  typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap");

const canRun = () => typeof window !== "undefined" && !isPrerendering();

function readStore(store, key) {
  try {
    const raw = window[store].getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeStore(store, key, value) {
  try {
    window[store].setItem(key, JSON.stringify(value));
  } catch (e) {
    /* storage full, disabled or blocked — carry on without it */
  }
}

function randomRef() {
  try {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (e) {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function deviceType() {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
    return "tablet";
  }
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
}

function currentPath() {
  return `${window.location.pathname}${window.location.search}`.slice(0, 500);
}

/**
 * Create (or return) the record for this visit.
 *
 * Called on mount, before any in-app navigation, so document.referrer and the
 * campaign parameters still describe how the visitor actually arrived.
 */
export function initLeadAttribution() {
  if (!canRun()) return null;

  const existing = readStore("sessionStorage", SESSION_KEY);
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const campaign = {};
  [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = params.get(key);
    if (value) campaign[key] = value.slice(0, 255);
  });

  // A visitor record that already exists means they've been here before.
  const visitor = readStore("localStorage", VISITOR_KEY);
  const firstSeenAt = visitor?.firstSeenAt || new Date().toISOString();
  if (!visitor) {
    writeStore("localStorage", VISITOR_KEY, { firstSeenAt });
  }

  const session = {
    ref: randomRef(),
    startedAt: new Date().toISOString(),
    firstSeenAt,
    isReturningVisitor: Boolean(visitor),
    referrer: (document.referrer || "").slice(0, 1000),
    landingPage: currentPath(),
    pageViews: 1,
    campaign,
  };

  writeStore("sessionStorage", SESSION_KEY, session);
  return session;
}

/** Count an in-app navigation towards this visit's page views. */
export function trackPageView() {
  if (!canRun()) return;

  const session = readStore("sessionStorage", SESSION_KEY) || initLeadAttribution();
  if (!session) return;

  session.pageViews = (session.pageViews || 0) + 1;
  writeStore("sessionStorage", SESSION_KEY, session);
}

function readClarityCookie(name) {
  // Clarity stores its user id in _clck and session id in _clsk, each as a
  // pipe-delimited value whose first segment is the id we want.
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    if (!match) return "";
    return decodeURIComponent(match[1]).split("|")[0].slice(0, 64);
  } catch (e) {
    return "";
  }
}

/**
 * Tag the Clarity session with our reference and ask Clarity who it thinks
 * this visitor is.
 *
 * `clarity("identify", ...)` resolves with { id, session, page }. Older builds
 * of the script return nothing, so cookies are the fallback — and the whole
 * thing races a timeout because a blocked or slow Clarity must never hold up
 * the enquiry.
 */
function collectClarityIds(ref) {
  const fromCookies = () => ({
    clarityUserId: readClarityCookie("_clck"),
    claritySessionId: readClarityCookie("_clsk"),
    clarityPageId: "",
  });

  if (typeof window.clarity !== "function") {
    return Promise.resolve(fromCookies());
  }

  const identified = new Promise((resolve) => {
    try {
      // Tag the session so it can be filtered for in the Clarity dashboard.
      window.clarity("set", "lead_ref", ref);
      window.clarity("set", "enquiry_submitted", "true");

      // Identify by our own opaque reference rather than the visitor's email:
      // it links the recording to the lead without handing personal data to a
      // third party.
      const result = window.clarity("identify", ref);
      if (result && typeof result.then === "function") {
        result
          .then((info) =>
            resolve({
              clarityUserId: (info?.id || "").toString().slice(0, 64),
              claritySessionId: (info?.session || "").toString().slice(0, 64),
              clarityPageId: (info?.page || "").toString().slice(0, 64),
            })
          )
          .catch(() => resolve(fromCookies()));
      } else {
        resolve(fromCookies());
      }
    } catch (e) {
      resolve(fromCookies());
    }
  });

  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(fromCookies()), CLARITY_TIMEOUT_MS);
  });

  return Promise.race([identified, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Build the `metadata` block sent with the directLead mutation.
 *
 * Keys are camelCase to match the GraphQL schema. Empty values are stripped so
 * the payload only carries what we actually know.
 */
export async function collectLeadMetadata() {
  if (!canRun()) return null;

  let metadata = {};

  try {
    const session = readStore("sessionStorage", SESSION_KEY) || initLeadAttribution();
    const startedAt = session?.startedAt ? new Date(session.startedAt) : null;
    const secondsOnSite = startedAt
      ? Math.max(0, Math.round((Date.now() - startedAt.getTime()) / 1000))
      : null;

    const clarity = await collectClarityIds(session?.ref || randomRef());

    metadata = {
      referrer: session?.referrer,
      landingPage: session?.landingPage,
      submittedPage: currentPath(),

      utmSource: session?.campaign?.utm_source,
      utmMedium: session?.campaign?.utm_medium,
      utmCampaign: session?.campaign?.utm_campaign,
      utmTerm: session?.campaign?.utm_term,
      utmContent: session?.campaign?.utm_content,
      gclid: session?.campaign?.gclid,
      fbclid: session?.campaign?.fbclid,
      msclkid: session?.campaign?.msclkid,

      deviceType: deviceType(),
      userAgent: (navigator.userAgent || "").slice(0, 500),
      language: navigator.language || "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,

      pageViews: session?.pageViews,
      timeOnSiteSeconds: secondsOnSite,
      isReturningVisitor: session?.isReturningVisitor,
      sessionStartedAt: session?.startedAt,
      firstSeenAt: session?.firstSeenAt,

      clarityRef: session?.ref,
      ...clarity,
    };
  } catch (e) {
    // Metadata is a bonus; an enquiry with none still needs to reach us.
    return null;
  }

  const cleaned = Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

  return Object.keys(cleaned).length ? cleaned : null;
}
